import {
	createFileRoute,
	useCanGoBack,
	useNavigate,
	useRouteContext,
	useRouter,
} from "@tanstack/react-router";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { Heading } from "@/components/heading";
import { PostForm, type PostFormSchemaValues } from "@/forms/post-form";
import type { postsNovoCreateMutation } from "./__generated__/postsNovoCreateMutation.graphql";
import type { postsNovoQuery } from "./__generated__/postsNovoQuery.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(general)/moderacao/posts/novo",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const router = useRouter();
	const canGoBack = useCanGoBack();
	const navigate = useNavigate({ from: Route.fullPath });
	const { club } = useRouteContext({ from: Route.id });

	const data = useLazyLoadQuery<postsNovoQuery>(
		graphql`
			query postsNovoQuery($club: String!) {
				club(slug: $club) {
					id
					groups {
						spaces {
							id
							name
						}
					}
				}
			}
		`,
		{ club: club.slug },
	);

	const spaces = data.club.groups.filter(Boolean).flatMap((g) => g.spaces);

	const [createPost] = useMutation<postsNovoCreateMutation>(graphql`
		mutation postsNovoCreateMutation($payload: CreatePostInput!) {
			createPost(payload: $payload) {
				postEdge {
					node {
						id
						title
						content
						slug
						url
						pinned
						pinnedUniversal
						publishDate
						commentCount
						commentPolicy
						hideMetadata
						votes
						userVote {
							vote
						}
						author {
							displayName
							username
							avatarFallback
							photo {
								signedUrl
							}
						}
						space {
							name
						}
						coverContent {
							__typename
						}
						attachments {
							id
							name
							signedUrl
							size
						}
					}
				}
			}
		}
	`);

	const onSubmit = (value: PostFormSchemaValues) => {
		return createPost({
			variables: {
				payload: {
					club: club.slug,
					title: value.title,
					content: JSON.stringify(value.content),
					space: value.space,
					cover: value.cover
						? { coverContent: value.cover.muxId ?? value.cover.id }
						: undefined,
					attachments: value.attachments.map((a) => ({ file: a.id })),
					commentPolicy: value.commentPolicy.toLowerCase(),
					pinned: value.pinned,
					pinnedUniversal: value.pinnedUniversal,
					hideMetadata: value.hideMetadata,
					notifyMembers: value.notifyUsers,
					scheduledPublishDate:
						value.schedule && value.scheduledDate
							? new Date(value.scheduledDate).toISOString()
							: undefined,
				},
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Post criado com sucesso!");

				if (canGoBack) {
					router.history.back();
				} else {
					navigate({ to: "/c/$club/moderacao" });
				}
			},
			onError: () => {
				toast.error(
					"Não foi possível criar o post no momento. Verifique os dados preenchidos e tente novamente.",
				);
			},
			updater: (store) => {
				const clubRecord = store.get(data.club.id);
				if (clubRecord) {
					clubRecord.invalidateRecord();
				}
			},
		});
	};

	return (
		<div className="size-full flex items-center justify-center py-4 md:py-8">
			<div className="p-6 w-full max-md:px-4 max-w-[700px] flex flex-col gap-6 border rounded-md">
				<Heading>Criar Post</Heading>

				<PostForm onSubmit={onSubmit} spaces={spaces} />
			</div>
		</div>
	);
}
